<template>
  <div class="employee-finance">
    <div v-if="showSelectEmployee" class="mb-4">
      <v-alert type="info" class="mb-3">
        {{ t("employeeFinance.alerts.selectEmployee") }}
      </v-alert>
      <v-card v-if="showEmployeeSelector" outlined class="pa-4">
        <v-select
          v-model="selectedEmployeeId"
          :items="employeeOptions"
          :label="t('employeeFinance.fields.employee')"
          item-title="title"
          item-value="value"
          :loading="employeesLoading"
          :disabled="employeesLoading"
          clearable
        >
          <template #item="{ props: itemProps, item }">
            <v-list-item v-bind="itemProps" :subtitle="item.raw.subtitle" />
          </template>
        </v-select>
        <v-alert v-if="employeesError" type="error" class="mt-3">
          {{ employeesError }}
        </v-alert>
      </v-card>
    </div>

    <template v-else>
      <v-card variant="flat" class="fin-hero mb-4">
        <div class="d-flex align-center ga-3">
          <v-icon color="tertiary" size="20">mdi-cash-multiple</v-icon>
          <div class="flex-grow-1 min-width-0">
            <div class="fin-hero__title">{{ t("employeeFinance.title") }}</div>
            <div class="fin-hero__subtitle">{{ t("employeeFinance.subtitle") }}</div>
          </div>
          <v-btn
            v-if="canCreateBonus"
            color="primary"
            variant="tonal"
            prepend-icon="mdi-gift-outline"
            @click="openCreateDialog"
          >
            {{ t("employeeFinance.actions.addBonus") }}
          </v-btn>
        </div>
      </v-card>

      <v-alert v-if="loadError" type="error" class="mb-3">{{ loadError }}</v-alert>
      <v-card outlined>
        <v-data-table
          :headers="headers"
          :items="payments"
          :loading="loading"
          class="payments-table"
          density="comfortable"
        >
          <template #item.payment_type="{ item }">
            {{ typeLabel(item.payment_type) }}
          </template>
          <template #item.amount="{ item }">
            {{ formatCurrency(item.amount, item.currency) }}
          </template>
          <template #item.period="{ item }">
            {{ formatPeriod(item.period_start, item.period_end) }}
          </template>
          <template #item.pay_date="{ item }">
            {{ formatDate(item.pay_date) }}
          </template>
          <template #item.status="{ item }">
            {{ statusLabel(item.status) }}
          </template>
          <template #item.actions="{ item }">
            <v-btn
              v-if="item.payroll_order_id && canManagePayments"
              size="small"
              variant="text"
              color="primary"
              :title="t('employeeFinance.actions.downloadOrder')"
              @click="downloadOrder(item.payroll_order_id)"
            >
              <v-icon size="18" class="mr-1">mdi-file-download-outline</v-icon>
              PDF
            </v-btn>
            <v-btn
              v-if="canManagePayments && isBonus(item.payment_type)"
              size="small"
              variant="text"
              color="primary"
              :title="t('app.actions.edit')"
              @click="openEditDialog(item)"
            >
              <v-icon size="18">mdi-pencil-outline</v-icon>
            </v-btn>
            <v-btn
              v-if="canManagePayments && isBonus(item.payment_type)"
              size="small"
              variant="text"
              color="error"
              :title="t('app.actions.delete')"
              @click="confirmDelete(item)"
            >
              <v-icon size="18">mdi-delete-outline</v-icon>
            </v-btn>
          </template>
          <template #loading>
            <v-sheet class="pa-6 text-center" color="transparent">
              <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
            </v-sheet>
          </template>
          <template #no-data>
            <v-sheet class="pa-4 text-center" color="transparent">
              {{ t("employeeFinance.empty") }}
            </v-sheet>
          </template>
        </v-data-table>
      </v-card>
    </template>

    <v-dialog v-model="createDialog.open" max-width="480">
      <v-card>
        <v-card-title>{{ t("employeeFinance.dialog.title") }}</v-card-title>
        <v-card-text>
          <v-alert v-if="createDialog.error" type="error" class="mb-3">
            {{ createDialog.error }}
          </v-alert>
          <v-select
            v-model="createDialog.form.payment_type"
            :items="bonusOptions"
            :label="t('employeeFinance.fields.type')"
            item-title="label"
            item-value="value"
            class="mb-3"
          ></v-select>
          <v-text-field
            v-model.number="createDialog.form.amount"
            type="number"
            :label="t('employeeFinance.fields.amount')"
            class="mb-3"
            min="0"
          ></v-text-field>
          <v-textarea
            v-model="createDialog.form.amount_words"
            :label="t('employeeFinance.fields.amountWords')"
            rows="2"
            auto-grow
            class="mb-3"
          ></v-textarea>
          <v-text-field
            v-model="createDialog.form.pay_date"
            type="date"
            :label="t('employeeFinance.fields.payDate')"
            class="mb-3"
          ></v-text-field>
          <v-textarea
            v-model="createDialog.form.notes"
            :label="t('employeeFinance.fields.notes')"
            rows="2"
            auto-grow
          ></v-textarea>
          <div class="text-caption text-medium-emphasis mt-2">
            {{ t("employeeFinance.hints.periodPreset") }}
          </div>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="closeCreateDialog">{{ t("app.actions.cancel") }}</v-btn>
          <v-btn
            color="primary"
            :loading="createDialog.loading"
            :disabled="!canSubmit"
            @click="submitBonus"
          >
            {{ t("app.actions.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editDialog.open" max-width="480">
      <v-card>
        <v-card-title>{{ t("employeeFinance.dialog.editTitle") }}</v-card-title>
        <v-card-text>
          <v-alert v-if="editDialog.error" type="error" class="mb-3">
            {{ editDialog.error }}
          </v-alert>
          <v-select
            v-model="editDialog.form.payment_type"
            :items="bonusOptions"
            :label="t('employeeFinance.fields.type')"
            item-title="label"
            item-value="value"
            class="mb-3"
          ></v-select>
          <v-text-field
            v-model.number="editDialog.form.amount"
            type="number"
            :label="t('employeeFinance.fields.amount')"
            class="mb-3"
            min="0"
          ></v-text-field>
          <v-textarea
            v-model="editDialog.form.amount_words"
            :label="t('employeeFinance.fields.amountWords')"
            rows="2"
            auto-grow
            class="mb-3"
          ></v-textarea>
          <v-text-field
            v-model="editDialog.form.pay_date"
            type="date"
            :label="t('employeeFinance.fields.payDate')"
            class="mb-3"
          ></v-text-field>
          <div class="d-flex flex-wrap ga-3">
            <v-text-field
              v-model="editDialog.form.period_start"
              type="date"
              :label="t('employeeFinance.fields.periodStart')"
              class="flex-1-1"
            ></v-text-field>
            <v-text-field
              v-model="editDialog.form.period_end"
              type="date"
              :label="t('employeeFinance.fields.periodEnd')"
              class="flex-1-1"
            ></v-text-field>
          </div>
          <v-textarea
            v-model="editDialog.form.notes"
            :label="t('employeeFinance.fields.notes')"
            rows="2"
            auto-grow
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="closeEditDialog">{{ t("app.actions.cancel") }}</v-btn>
          <v-btn
            color="primary"
            :loading="editDialog.loading"
            :disabled="!canSubmitEdit"
            @click="submitEdit"
          >
            {{ t("app.actions.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog.open" max-width="420">
      <v-card>
        <v-card-title>{{ t("employeeFinance.dialog.deleteTitle") }}</v-card-title>
        <v-card-text>
          <v-alert v-if="deleteDialog.error" type="error" class="mb-3">
            {{ deleteDialog.error }}
          </v-alert>
          <div class="text-body-2">
            {{ t("employeeFinance.messages.deleteConfirm") }}
          </div>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="closeDeleteDialog">{{ t("app.actions.cancel") }}</v-btn>
          <v-btn color="error" :loading="deleteDialog.loading" @click="submitDelete">
            {{ t("app.actions.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.open" :timeout="3500" color="success">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted } from "vue";
import axios from "axios";
import { useI18n } from "vue-i18n";
import {
  buildEmployeeOptions,
  canManagePayroll,
  resolveEffectiveCompanyId,
  resolveEffectiveEmployeeId,
  shouldShowEmployeeSelector,
} from "@/Utile/employeeFinanceUtils.mjs";
import { formatDisplayAmount } from "@/Utile/displayFormat.mjs";

const BONUS_TYPES = [
  { value: "bonus-stimulare-productivitate", labelKey: "employeeFinance.types.productivity" },
  { value: "bonus-individual", labelKey: "employeeFinance.types.individual" },
  { value: "bonus-general", labelKey: "employeeFinance.types.general" },
  { value: "allowance-other", labelKey: "employeeFinance.types.allowance" },
];
const STATUS_LABELS = {
  paid: "employeeFinance.status.paid",
  draft: "employeeFinance.status.draft",
  scheduled: "employeeFinance.status.scheduled",
  cancelled: "employeeFinance.status.cancelled",
};

const parseId = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const lastWorkingDayOfMonth = (dateInput) => {
  const base = dateInput ? new Date(dateInput) : new Date();
  if (Number.isNaN(base.getTime())) return null;
  const last = new Date(base.getFullYear(), base.getMonth() + 1, 0);
  while ([0, 6].includes(last.getDay())) {
    last.setDate(last.getDate() - 1);
  }
  const y = last.getFullYear();
  const m = String(last.getMonth() + 1).padStart(2, "0");
  const d = String(last.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export default defineComponent({
  name: "EmployeeFinance",
  props: {
    employeeId: {
      type: [Number, String],
      default: null,
    },
    companyId: {
      type: [Number, String],
      default: null,
    },
    canAddBonus: {
      type: Boolean,
      default: true,
    },
    onlyBonuses: {
      type: Boolean,
      default: false,
    },
    allowCompanyFallback: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const payments = ref([]);
    const loading = ref(false);
    const loadError = ref("");
    const employees = ref([]);
    const employeesLoading = ref(false);
    const employeesError = ref("");
    const userRole = ref((localStorage.getItem("user-role") || "").toLowerCase());
    const rolesSet = computed(() => {
      const raw = localStorage.getItem("user-roles");
      let parsed = [];
      try {
        parsed = JSON.parse(raw || "[]");
      } catch (e) {
        parsed = [];
      }
      const set = new Set();
      parsed.forEach((entry) => {
        if (!entry) return;
        const key = entry.key || entry.role || entry.name || entry;
        if (key) set.add(String(key).toLowerCase());
      });
      const legacy = (localStorage.getItem("user-role") || "").toLowerCase();
      if (legacy) set.add(legacy);
      return set;
    });
    const snackbar = ref({ open: false, message: "" });

    const selectedEmployeeId = ref(
      parseId(localStorage.getItem("hr-dashboard:selected-employee-id"))
    );
    const effectiveEmployeeId = computed(() =>
      resolveEffectiveEmployeeId(
        parseId(props.employeeId),
        selectedEmployeeId.value,
        parseId(localStorage.getItem("user-employee-id"))
      )
    );

    const effectiveCompanyId = computed(() =>
      resolveEffectiveCompanyId(
        parseId(props.companyId),
        parseId(localStorage.getItem("user-company-id"))
      )
    );

    const canManagePayments = computed(() => canManagePayroll(rolesSet.value, userRole.value));
    const canCreateBonus = computed(() => props.canAddBonus && canManagePayments.value);
    const canLoadCompanyPayments = computed(
      () =>
        props.allowCompanyFallback &&
        canManagePayments.value &&
        Boolean(effectiveCompanyId.value)
    );
    const showEmployeeSelector = computed(() =>
      shouldShowEmployeeSelector({
        effectiveEmployeeId: effectiveEmployeeId.value,
        allowCompanyFallback: canLoadCompanyPayments.value,
        canManagePayments: canManagePayments.value,
        effectiveCompanyId: effectiveCompanyId.value,
      })
    );
    const showSelectEmployee = showEmployeeSelector;

    const employeeOptions = computed(() => buildEmployeeOptions(employees.value || []));

    const headers = computed(() => [
      { title: t("employeeFinance.table.type"), value: "payment_type", sortable: false },
      { title: t("employeeFinance.table.amount"), value: "amount", sortable: false },
      { title: t("employeeFinance.table.period"), value: "period", sortable: false },
      { title: t("employeeFinance.table.payDate"), value: "pay_date", sortable: false },
      { title: t("employeeFinance.table.status"), value: "status", sortable: false },
      { title: "", value: "actions", sortable: false, width: 120 },
    ]);

    const bonusOptions = computed(() =>
      BONUS_TYPES.map((entry) => ({
        value: entry.value,
        label: t(entry.labelKey),
      }))
    );

    const createDialog = ref({
      open: false,
      loading: false,
      error: "",
      form: {
        payment_type: "bonus-individual",
        amount: null,
        amount_words: "",
        pay_date: lastWorkingDayOfMonth(),
        notes: "",
      },
    });
    const editDialog = ref({
      open: false,
      loading: false,
      error: "",
      form: {
        id: null,
        payment_type: "bonus-individual",
        amount: null,
        amount_words: "",
        pay_date: lastWorkingDayOfMonth(),
        period_start: null,
        period_end: null,
        notes: "",
      },
    });
    const deleteDialog = ref({ open: false, loading: false, error: "", id: null });

    const canSubmit = computed(() => {
      return (
        Number.isFinite(Number(createDialog.value.form.amount)) &&
        Number(createDialog.value.form.amount) > 0 &&
        Boolean(createDialog.value.form.payment_type) &&
        Boolean(createDialog.value.form.pay_date)
      );
    });

    const formatCurrency = (value, currency = "MDL") => {
      if (!Number.isFinite(Number(value))) return "-";
      const num = Number(value);
      return `${formatDisplayAmount(num)} ${currency}`;
    };
    const formatDate = (iso) => {
      if (!iso) return "-";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    };
    const formatPeriod = (start, end) => {
      if (!start || !end) return "-";
      return `${formatDate(start)} - ${formatDate(end)}`;
    };

    const isBonus = (type) => BONUS_TYPES.some((entry) => entry.value === type);

    const typeLabel = (type) => {
      const match = BONUS_TYPES.find((entry) => entry.value === type);
      if (match) return t(match.labelKey);
      if (type === "salary-monthly") return t("employeeFinance.types.salary");
      return type || "-";
    };

    const statusLabel = (status) => {
      const key = STATUS_LABELS[status];
      return key ? t(key) : status;
    };

    const fetchPayments = async () => {
      if (!effectiveEmployeeId.value && !canLoadCompanyPayments.value) return;
      loading.value = true;
      loadError.value = "";
      try {
        const params = {};
        if (effectiveEmployeeId.value) {
          params.employeeId = effectiveEmployeeId.value;
        }
        if (effectiveCompanyId.value) {
          params.companyId = effectiveCompanyId.value;
        }
        const { data } = await axios.get("/payroll/payments", { params });
        const items = Array.isArray(data?.items) ? data.items : [];
        payments.value = props.onlyBonuses
          ? items.filter((item) => BONUS_TYPES.some((entry) => entry.value === item.payment_type))
          : items;
      } catch (error) {
        loadError.value = t("employeeFinance.alerts.loadError");
      } finally {
        loading.value = false;
      }
    };

    const fetchEmployees = async () => {
      if (!showEmployeeSelector.value) return;
      employeesLoading.value = true;
      employeesError.value = "";
      try {
        const { data } = await axios.get("/candidati/employees", {
          params: { company_id: effectiveCompanyId.value },
        });
        employees.value = Array.isArray(data) ? data : [];
      } catch (error) {
        employeesError.value = t("employeeFinance.alerts.loadEmployeesError");
      } finally {
        employeesLoading.value = false;
      }
    };

    const openCreateDialog = () => {
      createDialog.value.error = "";
      createDialog.value.form = {
        payment_type: "bonus-individual",
        amount: null,
        amount_words: "",
        pay_date: lastWorkingDayOfMonth(),
        notes: "",
      };
      createDialog.value.open = true;
    };

    const closeCreateDialog = () => {
      createDialog.value.open = false;
      createDialog.value.error = "";
    };

    const submitBonus = async () => {
      if (!canSubmit.value || !effectiveEmployeeId.value) return;
      createDialog.value.loading = true;
      createDialog.value.error = "";
      try {
        const payload = {
          employee_id: effectiveEmployeeId.value,
          company_id: effectiveCompanyId.value || undefined,
          payment_type: createDialog.value.form.payment_type,
          amount: Number(createDialog.value.form.amount),
          amount_words: createDialog.value.form.amount_words
            ? String(createDialog.value.form.amount_words).trim()
            : null,
          pay_date: createDialog.value.form.pay_date,
          status: "paid",
          notes: createDialog.value.form.notes || null,
        };
        await axios.post("/payroll/payments", payload);
        snackbar.value = { open: true, message: t("employeeFinance.messages.bonusCreated") };
        createDialog.value.open = false;
        await fetchPayments();
      } catch (error) {
        createDialog.value.error = t("employeeFinance.messages.createError");
      } finally {
        createDialog.value.loading = false;
      }
    };

    const openEditDialog = (item) => {
      editDialog.value = {
        open: true,
        loading: false,
        error: "",
        form: {
          id: item?.id || null,
          payment_type: item?.payment_type || "bonus-individual",
          amount: item?.amount ?? null,
          amount_words: item?.amount_words || "",
          pay_date: item?.pay_date || lastWorkingDayOfMonth(),
          period_start: item?.period_start || null,
          period_end: item?.period_end || null,
          notes: item?.notes || "",
        },
      };
    };

    const closeEditDialog = () => {
      editDialog.value = {
        open: false,
        loading: false,
        error: "",
        form: {
          id: null,
          payment_type: "bonus-individual",
          amount: null,
          amount_words: "",
          pay_date: lastWorkingDayOfMonth(),
          period_start: null,
          period_end: null,
          notes: "",
        },
      };
    };

    const canSubmitEdit = computed(() => {
      const f = editDialog.value.form;
      return (
        Number.isFinite(Number(f.amount)) &&
        Number(f.amount) > 0 &&
        Boolean(f.pay_date) &&
        Boolean(f.payment_type)
      );
    });

    const submitEdit = async () => {
      const { form } = editDialog.value;
      if (!form.id || !canSubmitEdit.value) return;
      editDialog.value.loading = true;
      editDialog.value.error = "";
      try {
        await axios.patch(`/payroll/payments/${form.id}`, {
          payment_type: form.payment_type,
          amount: Number(form.amount),
          amount_words: form.amount_words ? String(form.amount_words).trim() : null,
          pay_date: form.pay_date,
          period_start: form.period_start || undefined,
          period_end: form.period_end || undefined,
          notes: form.notes || undefined,
        });
        snackbar.value = { open: true, message: t("employeeFinance.messages.updated") };
        closeEditDialog();
        fetchPayments();
      } catch (e) {
        editDialog.value.error = t("employeeFinance.messages.saveError");
      } finally {
        editDialog.value.loading = false;
      }
    };

    const confirmDelete = (item) => {
      deleteDialog.value = { open: true, loading: false, error: "", id: item?.id || null };
    };

    const closeDeleteDialog = () => {
      deleteDialog.value = { open: false, loading: false, error: "", id: null };
    };

    const submitDelete = async () => {
      if (!deleteDialog.value.id) return;
      deleteDialog.value.loading = true;
      deleteDialog.value.error = "";
      try {
        await axios.delete(`/payroll/payments/${deleteDialog.value.id}`);
        snackbar.value = { open: true, message: t("employeeFinance.messages.deleted") };
        closeDeleteDialog();
        fetchPayments();
      } catch (e) {
        deleteDialog.value.error = t("employeeFinance.messages.deleteError");
      } finally {
        deleteDialog.value.loading = false;
      }
    };

    const downloadOrder = async (orderId, format = "pdf") => {
      if (!orderId) return;
      const fmt = format === "doc" || format === "docx" ? "docx" : "pdf";
      try {
        const response = await axios.get(`/payroll/orders/${orderId}/download`, {
          params: { format: fmt },
          responseType: "blob",
        });
        const blob = new Blob([response.data], {
          type:
            fmt === "docx"
              ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              : "application/pdf",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `ordin-${orderId}.${fmt}`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (e) { /* ignore */ }
    };

    watch(
      () => [
        effectiveEmployeeId.value,
        canLoadCompanyPayments.value,
        showEmployeeSelector.value,
        effectiveCompanyId.value,
      ],
      ([nextEmployeeId, canLoadCompany, shouldLoadEmployees]) => {
        if (shouldLoadEmployees) {
          fetchEmployees();
        }
        if (nextEmployeeId || canLoadCompany) {
          fetchPayments();
        }
      },
      { immediate: true }
    );

    watch(
      () => selectedEmployeeId.value,
      (nextId) => {
        if (nextId) {
          localStorage.setItem("hr-dashboard:selected-employee-id", String(nextId));
        } else {
          localStorage.removeItem("hr-dashboard:selected-employee-id");
        }
      }
    );

    watch(
      () => effectiveCompanyId.value,
      (nextCompanyId, prevCompanyId) => {
        if (!nextCompanyId || nextCompanyId === prevCompanyId) return;
        if (!parseId(props.employeeId)) {
          selectedEmployeeId.value = null;
        }
      }
    );

    onMounted(() => {
      if (showEmployeeSelector.value) {
        fetchEmployees();
      }
      if (effectiveEmployeeId.value || canLoadCompanyPayments.value) {
        fetchPayments();
      }
    });

    return {
      t,
      payments,
      headers,
      loading,
      loadError,
      employees,
      employeesLoading,
      employeesError,
      employeeOptions,
      selectedEmployeeId,
      canCreateBonus,
      showSelectEmployee,
      showEmployeeSelector,
      createDialog,
      openCreateDialog,
      closeCreateDialog,
      submitBonus,
      typeLabel,
      formatCurrency,
      formatPeriod,
      formatDate,
      statusLabel,
      bonusOptions,
      snackbar,
      canSubmit,
      effectiveEmployeeId,
      downloadOrder,
      canManagePayments,
      isBonus,
      editDialog,
      openEditDialog,
      closeEditDialog,
      canSubmitEdit,
      submitEdit,
      deleteDialog,
      confirmDelete,
      closeDeleteDialog,
      submitDelete,
    };
  },
});
</script>

<style scoped>
.employee-finance .payments-table {
  min-height: 280px;
}

/* ── Hero card — standard pattern across ExternalDashboard ── */
.fin-hero {
  padding: 16px 20px;
  border-radius: var(--app-radius-base) !important;
  border-left: 4px solid rgb(var(--v-theme-tertiary)) !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06) !important;
  text-align: left;
}
.fin-hero__title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--app-text-strong);
  line-height: 1.3;
}
.fin-hero__subtitle {
  font-size: 0.82rem;
  color: var(--app-text-default);
  opacity: 0.7;
  margin-top: 2px;
}
.min-width-0 {
  min-width: 0;
}
</style>
